import React from 'react'
import { Paper } from '@mui/material'

import { Route, Routes } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import AuthenticationGuard from './components/auth/authentication-guard'
import AuthorizationGuard from './components/auth/authorization-guard'
import { isUsingProdDataRoutes } from './utils/api/servers'
import PageLoader from './components/auth/page-loader'
import HomePage from './pages/navigation/home-page'
import UserPage from './pages/navigation/user-page'
import NotFoundPage from './pages/navigation/not-found-page'
import FundingRateChart from './pages/funding-rate/funding-rate-chart'
import RiskCardList from './pages/risk/risk-card-list'
import MultiBalanceDataGrid from './pages/balance/multi-balance-data-grid'
import PnlPage from './pages/pnl/pnl-page'
import MultiExposureDataGrid from './pages/exposure/multi-exposure-data-grid'
import MultiExposureStatisticDataGrid from './pages/exposure-statistic/multi-exposure-statistic-data-grid'
import MultiBalancePlusDataGrid from './pages/balance-plus/multi-balance-plus-data-grid'
import TransferViewTabs from './pages/transfer/transfer-tabs'
import WithdrawView from './pages/withdraw/withdraw-view'
import ReconciliationView from './pages/reconciliation/reconciliation-view'
import StrategyStatusView from './pages/strategy-status/strategy-status-view'
import { DISPLAY_TYPE, WrappedStrategyTradeBookComposite } from './pages/_composite/strategy-trade-book-composite'
import SelectableStrategyComposite from './pages/_composite/selectable-strategy-composite'
import NavigationBar from './components/navigation/navigation-bar'

const COMMON_ROUTES = [
  { path: '/user', element: <AuthenticationGuard Component={UserPage}/> },
  { path: '/funding_rate', element: <AuthenticationGuard Component={FundingRateChart}/> },
  { path: '/risk', element: <AuthenticationGuard Component={RiskCardList}/> },
  { path: '/balance', element: <AuthenticationGuard Component={MultiBalanceDataGrid}/> },
  { path: '/pnl', element: <AuthenticationGuard Component={PnlPage}/> },
  { path: '/exposure', element: <AuthenticationGuard Component={MultiExposureDataGrid}/> },
  { path: '/exposure_statistic', element: <AuthenticationGuard Component={MultiExposureStatisticDataGrid}/> },
  { path: '/balance_plus', element: <AuthorizationGuard Component={MultiBalancePlusDataGrid} /> },
]

const LOCAL_DATA_ROUTES = [
  { path: '/simple_strategy_1', element: <AuthorizationGuard Component={WrappedStrategyTradeBookComposite} strategyId={null} strategyName='testnick' displayType={DISPLAY_TYPE.SIMPLE_NO_TRADES}/> },
  { path: '/strategy_1', element: <AuthorizationGuard Component={WrappedStrategyTradeBookComposite} strategyId={null} strategyName='testnick' displayType={DISPLAY_TYPE.DETAILED}/> },
]

const PROD_DATA_ROUTES = [
  { path: '/transfer_tabs', element: <AuthorizationGuard Component={TransferViewTabs}/> },
  { path: '/withdraw', element: <AuthorizationGuard Component={WithdrawView} /> },
  { path: '/reconciliation', element: <AuthorizationGuard Component={ReconciliationView} /> },
  { path: '/strategy_status', element: <AuthorizationGuard Component={StrategyStatusView} /> },
  { path: '/strategy', element: <AuthorizationGuard Component={SelectableStrategyComposite}/> },
]

const App: React.FunctionComponent = () => {
  const { isLoading, isAuthenticated } = useAuth0()

  if (isLoading) {
    return (
      <div style={{ width: '100%', height: '100%', backgroundColor: '#121212' }}>
        <PageLoader/>
      </div>
    )
  } else {
    const useProdDataRoutes = isUsingProdDataRoutes()
    const routes =
      useProdDataRoutes ? COMMON_ROUTES.concat(PROD_DATA_ROUTES) : COMMON_ROUTES.concat(LOCAL_DATA_ROUTES)

    return (
      <Paper square elevation={1} sx={{ height: 1, overflowY: 'auto' }}>
        { isAuthenticated ? <NavigationBar/> : null }
        <Routes>
          {routes.map(route => <Route key={route.path} path={route.path} element={route.element}/>)}
          {
            isAuthenticated
              ? <Route key='/' path='/' element={<AuthenticationGuard Component={UserPage}/>}/>
              : <Route key='/' path='/' element={<HomePage/>}/>
          }
          <Route key='*' path='*' element={<AuthenticationGuard Component={NotFoundPage}/>}/>
        </Routes>
      </Paper>
    )
  }
}

export default App
